//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

// Marketo Integration Docs: https://experienceleague.adobe.com/en/docs/marketo-developer/marketo/javascriptapi/forms-api-reference
// Examples: https://developers.marketo.com/rest-api/assets/forms/examples/

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { PrimaryLinkButton } from '@components/atoms/button/Button';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { BodyText } from '@components/atoms/text/Text';
import { Modal } from '@components/molecules/modal/Modal';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { MarketoIntegrationFeature } from '@marketo-integration/MarketoIntegrationFeature';
import {
  MARKETO_BASE_URL,
  MARKETO_FORM_ID,
  MARKETO_FORM_VERSION_ID,
  MARKETO_MUNCHKIN_ID,
  MARKETO_SCRIPT_ID,
  MARKETO_SDK_URL,
  MarketoIntegrationTrackingIds,
} from '@marketo-integration/marketoIntegrationConstants';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { useScriptLoaderQuery } from '@queries/useScriptLoaderQuery';
import { getClientId } from '@redux/selectors/getClientId';
import {
  getClientName,
  getCurrentUser,
  getUserEmail,
} from '@redux/selectors/getSessionInfo';
import { Mixins } from '@theme/mixins';

interface MarketoForm {
  vals: (values: Partial<MarketoFormValues>) => void;
  onSuccess: (cb: (values: MarketoFormValues) => void) => void;
  submit: () => void;
  submittable: (isSubmittable: boolean) => void;
}

interface MarketoIntegration {
  loadForm: (
    baseUrl: string,
    munchkinId: string,
    formVersionId: string | number,
    cb: (form: MarketoForm) => void,
  ) => void;
  whenReady: (callback: (form: MarketoForm) => void) => void;
}

declare global {
  interface Window {
    MktoForms2: MarketoIntegration | undefined;
  }
}

const loadMarketoForm = (
  initialValues: Partial<MarketoFormValues>,
  onSuccess?: (values: MarketoFormValues) => void,
) => {
  window.MktoForms2?.loadForm(
    MARKETO_BASE_URL,
    MARKETO_MUNCHKIN_ID,
    MARKETO_FORM_VERSION_ID,
    (form) => {
      form.vals(initialValues);

      form.onSuccess((values: MarketoFormValues) => {
        // once the form has been successfully sumitted, disable this SPECIFIC instance of the form to prevent
        // it from being submitted again
        form.submittable(false);

        if (onSuccess) {
          onSuccess(values);
        }

        // Return false to prevent the submission handler continuing with its own processing
        // Prevents redirect to new page
        return false;
      });
    },
  );
};

// Inputs defined by Marketo Form Editor
// To add more inputs we must work with the Bazaarvoice Marketing Automation team
interface MarketoFormValues {
  Company: string;
  Country_Code__c_lead: string;
  Email: string;
  FirstName: string;
  LastName: string;
  Phone: string;
  surveyQuestion1: string; // Used for ClientId
  surveyQuestion2: string; // Used for Feature
  Title: string;
}

const useMarketoForm = (
  feature: MarketoIntegrationFeature,
  onSubmit: () => void,
) => {
  const clientId = useSelector(getClientId);
  const clientName = useSelector(getClientName);
  const email = useSelector(getUserEmail);
  const { firstName, lastName } = useSelector(getCurrentUser);

  const [isFormRendering, setIsFormRendering] = useState(false);

  const {
    isFetched: isScriptLoaded,
    isFetching,
    refetch,
  } = useScriptLoaderQuery(
    [
      QueryKeyConstants.MarketoIntegraion,
      QueryKeyConstants.MarketoIntegrationScriptLoader,
    ],
    MARKETO_SCRIPT_ID,
    MARKETO_SDK_URL,
  );

  const initialValues: Partial<MarketoFormValues> = {
    Company: clientName,
    Email: email,
    FirstName: firstName,
    LastName: lastName,
    surveyQuestion1: clientId.toString(),
    surveyQuestion2: feature,
  };

  const isLoadingForm = isFetching || isFormRendering;

  function loadForm() {
    setIsFormRendering(true);

    loadMarketoForm(initialValues, onSubmit);

    window.MktoForms2?.whenReady(() => {
      setIsFormRendering(false);
    });
  }

  const renderMarketoForm = () => {
    if (!isScriptLoaded) {
      void refetch().then(() => {
        loadForm();
      });
    } else {
      loadForm();
    }
  };

  return { renderMarketoForm, isLoadingForm };
};

export function MarketoIntegration({
  className,
  feature,
}: {
  className?: string;
  feature: MarketoIntegrationFeature;
}) {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isLoadingForm, renderMarketoForm } = useMarketoForm(feature, () => {
    dispatch(
      toastNotificationsActions.addToastNotification({
        title: 'Information Submitted',
        message: 'A Bazaarvoice team member will reach out to you shortly.',
        type: ToastType.SUCCESS,
        options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
      }),
    );
    setIsModalOpen(false);
  });

  return (
    <>
      <PrimaryLinkButton
        className={className}
        onClick={() => {
          setIsModalOpen(true);
          renderMarketoForm();
        }}
        trackingId={MarketoIntegrationTrackingIds.OPEN_MODAL_BUTTON}
      >
        Contact us to learn more.
      </PrimaryLinkButton>
      <Modal
        closeModal={() => setIsModalOpen(false)}
        contents={
          <Container>
            <BodyText>
              Enter your information below and a Bazaarvoice team member will
              reach out.
            </BodyText>
            {isLoadingForm && <CenteredSpinner />}
            <StyledForm id={MARKETO_FORM_ID} />
          </Container>
        }
        isModalOpen={isModalOpen}
        primaryButtonInfo={{
          trackingId: MarketoIntegrationTrackingIds.MODAL_FORM_SUBMIT_BUTTON,
          onClick: () => {
            window.MktoForms2?.whenReady((form) => {
              form.submit();
            });
          },
          buttonContent: 'Submit',
        }}
        secondaryButtonInfo={{
          trackingId: MarketoIntegrationTrackingIds.MODAL_CANCEL_BUTTON,
        }}
        title='Learn more about additional Vibe features'
        trackingId={MarketoIntegrationTrackingIds.MODAL}
        width='wide'
      />
    </>
  );
}

// This style attr is hack needed to position the Modal above legacy angular modals.
// We are using the trackingId to select the modal and the previous sibling to the
// modal that has the data-state='open' attr to apply the z-index.
// This should be replaced when we migrate away from angular modals in Galleries and UIA.
export function MarketoIntegrationModalStyle() {
  return (
    <style>
      {`[data-tracking-id=${MarketoIntegrationTrackingIds.MODAL}] { z-index: 100000;}`}
      {`[data-state='open']:has(+ [data-tracking-id=${MarketoIntegrationTrackingIds.MODAL}]) { z-index: 10000;}`}
    </style>
  );
}

const Container = styled.div`
  max-width: 100%;
  display: flex;
  flex-direction: column;
`;

// Important styles are required because Marketo adds inline style props to
// all form elements
const StyledForm = styled.form(
  ({ theme }) => `
  width: 100% !important;

  & input {
    width: calc(${theme.spacings[6]} * 12) !important;
  }

  & .mktoAsterix {
    color: ${theme.colors.aliases.bodyPrimary} !important;
  }

  & label {
    color: ${theme.colors.aliases.bodyPrimary} !important;
    padding: ${theme.spacings[2]} 0 ${theme.spacings[1]} !important;
  }

  /* Hide the form button to use modal Submit button as the form submit button */
  & button[type="submit"],
  & .mktoButtonRow {
    ${Mixins.VisuallyHidden};
  }
`,
);
